import React from 'react';
import { saveAs } from 'file-saver';
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import { getSpeakerColor, newFormatTime } from '../../../../utils/calcs';
import { useTranslation } from 'react-i18next';

const MAX_WIDTH = 500;
const LINE_HEIGHT = 20;
const PAGE_HEIGHT = 800;
const MARGIN_X = 50;
const MARGIN_Y = 50;

const ExportToPDF = (groupedTranscriptions, speakerNames, selections, colors, textCodes, analysis, teamMembers) => {

    PDFDocument.create().then(async (pdfDoc) => {
        const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
        const helveticaBoldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

        let page = pdfDoc.addPage([595, 842]);
        let yPosition = PAGE_HEIGHT;
        let annotationCounter = 1;
        let annotationMap = [];
        const addFooter = (page, pdfDoc) => {

            page.drawText("AIQDA.com", {
                x: MARGIN_X,
                y: 20,
                size: 10,
                font: helveticaFont,
                color: rgb(0.73, 0.73, 0.73)
            });
        };

        // Add footer to the first page
        addFooter(page, pdfDoc);

        groupedTranscriptions.forEach(group => {
            const speakerColor = getSpeakerColor(speakerNames, speakerNames[group.speaker], colors);
            const color = rgb(
                parseInt(speakerColor.slice(1, 3), 16) / 255,
                parseInt(speakerColor.slice(3, 5), 16) / 255,
                parseInt(speakerColor.slice(5, 7), 16) / 255
            );

            if (yPosition < MARGIN_Y) {
                page = pdfDoc.addPage([595, 842]);
                yPosition = PAGE_HEIGHT;
                addFooter(page, pdfDoc); // Add footer to each new page
            }

            // Draw speaker name with the speaker's color
            const speakerName = speakerNames[group.speaker] || " ";
            page.drawText(speakerName, {
                x: MARGIN_X,
                y: yPosition,
                size: 12,
                color: color,
                font: helveticaBoldFont // Using bold font for speaker name
            });

            // Calculate width of speaker name to position the timestamp after it
            const speakerNameWidth = helveticaBoldFont.widthOfTextAtSize(speakerName, 12);

            // Draw timestamps in gray and smaller font size
            const timeStampText = `${group.startTime ? newFormatTime(group.startTime) : ''}-${group.endTime ? newFormatTime(group.endTime) : ''}`;
            page.drawText(timeStampText, {
                x: MARGIN_X + speakerNameWidth + 10, // Add some spacing between name and timestamp
                y: yPosition,
                size: 9, // Smaller size for timestamps
                color: rgb(0.5, 0.5, 0.5), // Gray color for timestamps
                font: helveticaFont
            });
            yPosition -= LINE_HEIGHT;

            const sortedSelections = selections?.filter(sel => sel.uuid === group.uuid).sort((a, b) => a.start - b.start);
            let xPosition = MARGIN_X;
            let highlightColor = null;
            let charIndex = 0;

            const words = group.lines.split(/(\s+)/);  // Split by words and spaces
            words.forEach(word => {
                const wordWidth = helveticaFont.widthOfTextAtSize(word, 12);

                sortedSelections?.forEach((selection) => {
                    if (selection.start <= charIndex && selection.end > charIndex) {
                        const textCode = textCodes.find(code => code.uuid === selection.name);
                        const author = teamMembers?.find(member => member.user_uuid === selection.author);
                        if (textCode) {
                            const col = textCode.color.replace('#', '');
                            highlightColor = rgb(
                                parseInt(col.slice(0, 2), 16) / 255,
                                parseInt(col.slice(2, 4), 16) / 255,
                                parseInt(col.slice(4, 6), 16) / 255
                            );
                            if (selection.end <= charIndex + word.length) {
                                annotationMap.push({
                                    number: annotationCounter,
                                    codeName: textCode.name,
                                    author: author?.user_name || 'Unknown'
                                });
                                page.drawText(`${annotationCounter}`, {
                                    x: xPosition + wordWidth - 2, // Adjusted position
                                    y: yPosition + 14,
                                    size: 8,
                                    color: rgb(0, 0, 0),
                                    font: helveticaBoldFont,
                                });
                                annotationCounter++;
                            }
                        }
                    }
                    if (selection.end <= charIndex) {
                        highlightColor = null;
                    }
                });

                if (xPosition + wordWidth > MAX_WIDTH) {
                    xPosition = MARGIN_X;
                    yPosition -= LINE_HEIGHT;
                    if (yPosition < MARGIN_Y) {
                        page = pdfDoc.addPage([595, 842]);
                        yPosition = PAGE_HEIGHT;
                        addFooter(page, pdfDoc); // Add footer to each new page
                    }
                }

                if (highlightColor) {
                    page.drawRectangle({
                        x: xPosition,
                        y: yPosition - 2,
                        width: wordWidth,
                        height: 18,
                        color: highlightColor,
                        opacity: 0.4
                    });
                }

                page.drawText(word, {
                    x: xPosition,
                    y: yPosition + 6,
                    size: 12,
                    color: rgb(0, 0, 0),
                    font: helveticaFont
                });
                xPosition += wordWidth;
                charIndex += word.length;
            });

            yPosition -= LINE_HEIGHT;
        });

        page = pdfDoc.addPage([595, 842]);
        yPosition = PAGE_HEIGHT;

        // Add footer to the annotations page
        addFooter(page, pdfDoc);

        page.drawText("Annotations:", {
            x: MARGIN_X,
            y: yPosition,
            size: 14,
            font: helveticaFont
        });
        yPosition -= LINE_HEIGHT;

        annotationMap.forEach((annotation) => {
            page.drawText(`[${annotation.number}] ${annotation.codeName} - ${annotation.author}`, {
                x: MARGIN_X,
                y: yPosition,
                size: 10,
                font: helveticaFont
            });
            yPosition -= LINE_HEIGHT;
        });

        pdfDoc.save().then((pdfBytes) => {
            const blob = new Blob([pdfBytes], { type: 'application/pdf' });
            saveAs(blob, `${analysis.name}_transcription.pdf`);
        }).catch((error) => {
            console.error('Error saving PDF:', error);
        });
    }).catch((error) => {
        console.error('Error creating PDF:', error);
    });
};

export default ExportToPDF;
